import React, { useMemo, useState } from 'react';
import { ListIcon, SlidersHorizontalIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { CategoryKey } from './categoryData';
type CatalogView = 'grid' | 'list';
type SortOption = 'default' | 'name-asc' | 'name-desc';
type BeveragesCatalogProps = {
  onCategorySelect: (categoryKey: CategoryKey) => void;
};
type BeverageProduct = {
  name: string;
  collection: string;
  image: string;
};
const EXPLORER_CATEGORIES: {
  key: CategoryKey;
  label: string;
  count: number;
}[] = [{
  key: 'alimentation',
  label: 'Alimentation',
  count: 47
}, {
  key: 'boisson',
  label: 'Boisson',
  count: 25
}, {
  key: 'electronique',
  label: 'Électronique',
  count: 6
}, {
  key: 'maison-decoration',
  label: 'Maison et Décoration',
  count: 16
}];
const PRODUCTS: BeverageProduct[] = [{
  name: 'Champagne Brut Réserve – Coffret prestige',
  collection: 'Champagne · Brut',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/f62e83a4-dd4b-4f91-8c23-142abdaf996b.jpg'
}, {
  name: 'Champagne Brut Cuvée Tradition',
  collection: 'Champagne · Brut',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/6a8a2e9d-69bb-45be-b4d5-61dcd44c802d.jpg'
}, {
  name: 'Champagne Impérial – Sélection maison',
  collection: 'Champagne · Prestige',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/da785dfe-7dd4-4486-ae93-812f00ec25d0.jpg'
}, {
  name: 'Vin effervescent Rosé – Cuvée élégance',
  collection: 'Effervescent · Rosé',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/34fff276-83ea-48bf-85b2-cd4d36162e5a.jpg'
}, {
  name: 'Champagne Brut Grande Réserve',
  collection: 'Champagne · Brut',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/f62e83a4-dd4b-4f91-8c23-142abdaf996b.jpg'
}, {
  name: 'Champagne Cuvée Signature',
  collection: 'Champagne · Prestige',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/6a8a2e9d-69bb-45be-b4d5-61dcd44c802d.jpg'
}];
export function BeveragesCatalog({
  onCategorySelect
}: BeveragesCatalogProps) {
  const [sort, setSort] = useState<SortOption>('default');
  const [view, setView] = useState<CatalogView>('grid');
  const products = useMemo(() => {
    if (sort === 'default') return PRODUCTS;
    return [...PRODUCTS].sort((first, second) => {
      const result = first.name.localeCompare(second.name, 'fr');
      return sort === 'name-asc' ? result : -result;
    });
  }, [sort]);
  return <section className="mt-8 overflow-hidden rounded-2xl bg-white shadow-[0_18px_45px_rgba(10,51,32,0.12)]" aria-labelledby="beverages-catalog-title" data-id="element-32" data-mp="3vpbfb3p1xvkl">
      <div className="relative overflow-hidden bg-[#f4fae8] px-6 py-12 sm:px-10 lg:px-14 lg:py-16" data-id="element-33" data-mp="3gr98mkmq4tz4">
        <div className="absolute -right-10 -top-16 h-56 w-56 rounded-full border-[26px] border-ardis-yellow/35" aria-hidden="true" data-id="element-34" data-mp="3lpua0ndppjp5" />
        <div className="absolute bottom-0 right-[15%] h-24 w-24 rounded-full border-[18px] border-ardis-green/10" aria-hidden="true" data-id="element-35" data-mp="25f014x723609" />
        <div className="relative" data-id="element-36" data-mp="sdu56c5pm6qm">
          <p className="text-sm text-gray-500" data-id="element-37" data-mp="scsi19bf5tsj">Accueil <span aria-hidden="true" data-id="element-38" data-mp="nf95cduddp2p">›</span> <span className="font-medium text-ardis-green" data-id="element-39" data-mp="ne7ht9w63a43">Boisson</span></p>
          <h3 id="beverages-catalog-title" className="mt-4 font-display text-4xl font-bold tracking-tight text-ardis-green sm:text-5xl" data-id="element-40" data-mp="11es0m62315xl">Boisson</h3>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-gray-600 sm:text-base" data-id="element-41" data-mp="xdycbbkq9p46">Découvrez notre sélection de champagnes, boissons festives et références premium disponibles sur devis.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[230px_minmax(0,1fr)]" data-id="element-42" data-mp="4moj3f96ei7v">
        <aside className="border-b border-gray-100 bg-gray-50/70 p-6 lg:border-b-0 lg:border-r lg:p-8" aria-label="Explorer les catégories" data-id="element-43" data-mp="igz8lyszygez">
          <h4 className="font-display text-xl font-bold text-ardis-green" data-id="element-44" data-mp="dnmp2336czt3">Explorer</h4>
          <ul className="mt-5 space-y-1 border-t border-gray-200 pt-4" data-id="element-45" data-mp="3m36aifxltdxl">
            {EXPLORER_CATEGORIES.map(category => {
            const isCurrent = category.key === 'boisson';
            return <li key={category.key} data-id="element-46" data-mp="3uuq0l96nih2p">
                  <button type="button" onClick={() => onCategorySelect(category.key)} aria-current={isCurrent ? 'page' : undefined} className={`flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ardis-blue focus:ring-offset-2 ${isCurrent ? 'text-ardis-green' : 'text-gray-700 hover:bg-white hover:text-ardis-green'}`} data-id="element-47" data-mp="3mc3ocdt4syq">
                    <span className="flex items-center gap-2" data-id="element-48" data-mp="mc20t651lqtt"><span className="text-ardis-yellow" aria-hidden="true" data-id="element-49" data-mp="mf6agj1a1erv">›</span>{category.label}</span>
                    <span className={isCurrent ? 'text-ardis-yellow' : 'text-gray-400'} data-id="element-50" data-mp="rad9hdjlpvag">({category.count})</span>
                  </button>
                </li>;
          })}
          </ul>
        </aside>

        <div className="min-w-0 p-6 sm:p-8" data-id="element-51" data-mp="2t5w09nak2jnv">
          <div className="flex flex-col gap-4 border-b border-gray-100 pb-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-52" data-mp="2y1ump5cbprln">
            <p className="text-sm font-medium text-gray-700" data-id="element-53" data-mp="2qko8sbnqoe8p">Affichage de <span className="font-semibold text-ardis-green" data-id="element-54" data-mp="2lrv7xyve5q1w">1–{products.length}</span> sur 25 résultats</p>
            <div className="flex flex-wrap items-center gap-2" aria-label="Options d’affichage et de tri" data-id="element-55" data-mp="2gsilvgccgn0d">
              <div className="flex rounded-md border border-gray-200 bg-white p-0.5" role="group" aria-label="Mode d’affichage" data-id="element-56" data-mp="2bv9y908ffqrq">
                <button type="button" aria-label="Afficher en grille" aria-pressed={view === 'grid'} onClick={() => setView('grid')} className={`rounded p-2 focus:outline-none focus:ring-2 focus:ring-ardis-blue ${view === 'grid' ? 'bg-ardis-green text-white' : 'text-gray-500 hover:text-ardis-green'}`} data-id="element-57" data-mp="39h7ltc1nymaj"><div className="h-4 w-4" aria-hidden="true" data-id="element-58" data-mp="2uk6sj43tqj9y" /></button>
                <button type="button" aria-label="Afficher en liste" aria-pressed={view === 'list'} onClick={() => setView('list')} className={`rounded p-2 focus:outline-none focus:ring-2 focus:ring-ardis-blue ${view === 'list' ? 'bg-ardis-green text-white' : 'text-gray-500 hover:text-ardis-green'}`} data-id="element-59" data-mp="1o4wedwzzwy88"><ListIcon className="h-4 w-4" aria-hidden="true" data-id="element-60" data-mp="f87rag3h0rul" /></button>
              </div>
              <label className="relative flex items-center" data-id="element-61" data-mp="186mwqnzz2ad">
                <SlidersHorizontalIcon className="pointer-events-none absolute left-3 h-4 w-4 text-ardis-green" aria-hidden="true" data-id="element-62" data-mp="1a10fjwxvs4f" />
                <span className="sr-only" data-id="element-63" data-mp="3nnrdww9mzc0o">Trier les produits</span>
                <select value={sort} onChange={event => setSort(event.target.value as SortOption)} className="h-10 appearance-none rounded-md border border-gray-200 bg-white py-2 pl-9 pr-9 text-sm font-medium text-gray-700 outline-none focus:border-ardis-blue focus:ring-2 focus:ring-ardis-blue/20" data-id="element-64" data-mp="3nlenrvtq5vcj">
                  <option value="default" data-id="element-65" data-mp="3jjth4s7sadas">Tri par défaut</option>
                  <option value="name-asc" data-id="element-66" data-mp="34pybrw6u764c">Nom, A à Z</option>
                  <option value="name-desc" data-id="element-67" data-mp="39ngm9r0i5teg">Nom, Z à A</option>
                </select>
              </label>
            </div>
          </div>

          <motion.div layout className={view === 'grid' ? 'mt-8 grid gap-x-6 gap-y-10 sm:grid-cols-2 xl:grid-cols-3' : 'mt-6 space-y-4'} data-id="element-68" data-mp="2q2pwaj6hwemk">
            {products.map((product, index) => {
            const quoteUrl = `mailto:info@lepiandistribution.com?subject=${encodeURIComponent(`Demande de devis — ${product.name}`)}`;
            return <motion.article layout key={product.name} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.28,
              delay: index * 0.04
            }} className={view === 'grid' ? 'flex flex-col' : 'flex flex-col gap-5 rounded-xl border border-gray-100 p-4 sm:flex-row sm:items-center'} data-id="element-69" data-mp="2v346a6rnpyn8" data-mpi={index}>
                  <div className={view === 'grid' ? 'aspect-square overflow-hidden bg-white' : 'h-36 w-full flex-shrink-0 overflow-hidden bg-white sm:w-44'} data-id="element-70" data-mp="3sojmapss6tit"><img src={product.image} alt={product.name} className="h-full w-full object-contain" data-id="element-71" data-mp="3nr02ladbdojh" /></div>
                  <div className={view === 'grid' ? 'flex flex-1 flex-col pt-4' : 'flex flex-1 flex-col'} data-id="element-72" data-mp="28d4edt4hiinf">
                    <p className="text-xs font-medium text-ardis-green/70" data-id="element-73" data-mp="2damh31rws1mz">{product.collection}</p>
                    <h5 className="mt-2 font-display text-sm font-semibold leading-relaxed text-gray-900" data-id="element-74" data-mp="2icbtolus4nd6">{product.name}</h5>
                    <a href={quoteUrl} className="mt-4 inline-flex w-fit items-center justify-center rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2" data-id="element-75" data-mp="1pmwhvtvaiqg7">Demandez un devis</a>
                  </div>
                </motion.article>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}